import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Main } from '@/components/layout/main';
import { PageHeader } from '@/components/layout/page-header';
import { DataTable } from '@/components/table';
import { DataTableColumnHeader } from '@/components/data-table-column-header';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { superAdminService } from '@/api/super-admin';
import type {
  LawyerProfile,
  LawyerProfileApprovalStatus,
  ListingRequestDashboard,
} from '@/types/super-admin';
import { handleServerError } from '@/utils/handle-server-error';
import { toast } from '@/hooks/use-toast';
import { ColumnDef } from '@tanstack/react-table';
import {
  Check,
  Clock,
  Download,
  Eye,
  Inbox,
  MoreHorizontal,
  ThumbsUp,
  X,
} from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

type StatusFilter = 'all' | LawyerProfileApprovalStatus;

const STATUS_BADGE: Record<LawyerProfileApprovalStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-300',
  approved: 'bg-green-50 text-green-700 border-green-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
};

/**
 * Moderation queue for the public "référencez-moi" form on the landing page.
 *
 * Every row here is a LawyerProfile with submission_source='public_request' —
 * it is invisible on the public directory until it is approved from this page.
 */
export default function ListingRequestsManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const openProfile = (id: string) =>
    navigate({ to: '/super-admin-lawyer-profiles/$id', params: { id } });

  const [requests, setRequests] = useState<LawyerProfile[]>([]);
  const [stats, setStats] = useState<ListingRequestDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pagesCnt, setPagesCnt] = useState(1);
  const [params, setParams] = useState<{ page: number; page_size: number; ordering?: string }>({
    page: 0,
    page_size: 10,
  });

  // Pending is the working view: it is what the team has to act on.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [nameFilter, setNameFilter] = useState('');

  const [actionRequest, setActionRequest] = useState<LawyerProfile | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [sendReviewLink, setSendReviewLink] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const apiParams: Record<string, unknown> = {
        submission_source: 'public_request',
        page_size: params.page_size,
        page: params.page * params.page_size,
      };
      if (statusFilter !== 'all') apiParams.approval_status = statusFilter;
      if (nameFilter.trim()) apiParams.full_name = nameFilter.trim();

      const data = await superAdminService.getLawyerProfiles(apiParams as never);
      setRequests(data.results);
      setPagesCnt(Math.ceil((data.count || data.results.length) / params.page_size));
    } catch (error) {
      handleServerError(error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [params, statusFilter, nameFilter]);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await superAdminService.getListingRequestDashboard({ limit: 5 }));
    } catch (error) {
      handleServerError(error);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refresh = () => {
    fetchRequests();
    fetchStats();
  };

  const handleApprove = async () => {
    if (!actionRequest) return;
    try {
      setSubmitting(true);
      const result = await superAdminService.approveListingRequest(actionRequest.id, sendReviewLink);
      setShowApproveDialog(false);
      setActionRequest(null);
      refresh();
      toast({
        title: t('lr_approved'),
        description: sendReviewLink && !result.review_email_sent ? t('lr_review_email_failed') : undefined,
      });
    } catch (error) {
      handleServerError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!actionRequest) return;
    try {
      setSubmitting(true);
      await superAdminService.rejectListingRequest(actionRequest.id, rejectionReason.trim());
      setShowRejectDialog(false);
      setActionRequest(null);
      setRejectionReason('');
      refresh();
      toast({ title: t('lr_rejected') });
    } catch (error) {
      handleServerError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await superAdminService.exportListingRequests(
        statusFilter === 'all' ? undefined : statusFilter,
      );
      // The API sends a Content-Disposition filename, but a blob download has to
      // name the file itself, so mirror the server's naming here.
      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `listing-requests-${statusFilter}-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      handleServerError(error);
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnDef<LawyerProfile>[] = useMemo(() => [
    {
      id: 'full_name',
      accessorKey: 'full_name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('lp_full_name')} />,
      cell: ({ row }) => {
        const p = row.original;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{p.full_name}</span>
            {p.full_name_fr && p.full_name_fr !== p.full_name && (
              <span className="text-xs text-muted-foreground">{p.full_name_fr}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'bar_city',
      accessorKey: 'bar_city',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('bar_city')} />,
      cell: ({ row }) => <span className="capitalize">{row.original.bar_city || '—'}</span>,
    },
    {
      id: 'contact',
      accessorKey: 'email_public',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('lr_contact')} />,
      cell: ({ row }) => {
        const p = row.original;
        return (
          <div className="flex flex-col gap-0.5 text-sm">
            <span>{p.email_public || '—'}</span>
            {p.phone && <span className="text-xs text-muted-foreground">{p.phone}</span>}
          </div>
        );
      },
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('lr_submitted_at')} />,
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.created_at * 1000).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'approval_status',
      accessorKey: 'approval_status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('lr_status')} />,
      cell: ({ row }) => {
        const status = row.original.approval_status;
        return (
          <Badge variant="outline" className={STATUS_BADGE[status]}>
            {t(`lr_status_${status}`)}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: () => <div className="text-center">{t('actions')}</div>,
      cell: ({ row }) => {
        const request = row.original;
        const isPending = request.approval_status === 'pending';
        return (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openProfile(request.id)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('lp_view_details')}
                </DropdownMenuItem>
                {isPending && (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        setActionRequest(request);
                        setSendReviewLink(true);
                        setShowApproveDialog(true);
                      }}
                    >
                      <ThumbsUp className="mr-2 h-4 w-4 text-green-600" />
                      <span className="text-green-600">{t('lr_approve')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setActionRequest(request);
                        setRejectionReason('');
                        setShowRejectDialog(true);
                      }}
                      className="text-red-600"
                    >
                      <X className="mr-2 h-4 w-4" />
                      {t('lr_reject')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ], [t, navigate]);

  return (
    <div className="flex h-screen">
      <div className="flex-1">
        <Main>
          <div className="space-y-6">
            <PageHeader
              leading={
                <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Inbox className="h-5 w-5" />
                </span>
              }
              title={t('lr_page_title')}
              description={t('lr_page_desc')}
              actions={
                <Button variant="outline" onClick={handleExport} disabled={exporting}>
                  <Download className="me-2 h-4 w-4" />
                  {t('lr_export_csv')}
                </Button>
              }
            />

            {/* Counters */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label={t('lr_status_pending')}
                value={stats?.totals.pending ?? 0}
                icon={<Clock className="h-4 w-4 text-amber-600" />}
                highlight={(stats?.totals.pending ?? 0) > 0}
              />
              <StatCard
                label={t('lr_status_approved')}
                value={stats?.totals.approved ?? 0}
                icon={<Check className="h-4 w-4 text-green-600" />}
              />
              <StatCard
                label={t('lr_status_rejected')}
                value={stats?.totals.rejected ?? 0}
                icon={<X className="h-4 w-4 text-red-600" />}
              />
              <StatCard
                label={t('lr_recent_submitted', { days: stats?.window_days ?? 30 })}
                value={stats?.recent.submitted ?? 0}
                icon={<Inbox className="h-4 w-4 text-primary" />}
              />
            </div>

            {(stats?.oldest_pending_days ?? 0) > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <Clock className="h-4 w-4 shrink-0" />
                {t('lr_oldest_pending', { days: stats?.oldest_pending_days })}
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <Input
                placeholder={t('lp_search_name')}
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                className="w-56"
              />
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder={t('lr_status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{t('lr_status_pending')}</SelectItem>
                  <SelectItem value="approved">{t('lr_status_approved')}</SelectItem>
                  <SelectItem value="rejected">{t('lr_status_rejected')}</SelectItem>
                  <SelectItem value="all">{t('lp_all')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DataTable
              columns={columns}
              data={requests}
              pages={pagesCnt}
              params={params}
              setParams={setParams}
              showPagination={true}
              onRowClick={(row: LawyerProfile) => openProfile(row.id)}
              storageKey="super-admin-listing-requests-table"
            />

            {!loading && requests.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">{t('lr_empty')}</p>
            )}
          </div>

          {/* Approve */}
          <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('lr_approve_title')}</DialogTitle>
                <DialogDescription>
                  {t('lr_approve_desc')} <strong>{actionRequest?.full_name}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-start gap-3 py-2">
                <Checkbox
                  id="send-review-link"
                  checked={sendReviewLink}
                  onCheckedChange={(checked) => setSendReviewLink(checked === true)}
                />
                <div className="grid gap-1">
                  <Label htmlFor="send-review-link" className="cursor-pointer">
                    {t('lr_send_review_link')}
                  </Label>
                  <p className="text-xs text-muted-foreground">{t('lr_send_review_link_hint')}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button onClick={handleApprove} disabled={submitting}>
                  {t('lr_approve')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reject */}
          <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('lr_reject_title')}</DialogTitle>
                <DialogDescription>
                  {t('lr_reject_desc')} <strong>{actionRequest?.full_name}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-2 py-2">
                <Label htmlFor="rejection-reason">{t('lr_rejection_reason')}</Label>
                <Textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder={t('lr_rejection_reason_placeholder')}
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button variant="destructive" onClick={handleReject} disabled={submitting}>
                  {t('lr_reject')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Main>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  highlight,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? 'border-amber-300' : undefined}>
      <CardContent className="flex items-center justify-between p-4">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-2xl font-semibold">{value}</p>
        </div>
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
          {icon}
        </span>
      </CardContent>
    </Card>
  );
}
